import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, post } from './client'
import type {
  Run,
  RunsListResponse,
  RunsFilterParams,
  CreateRunRequest,
  RunLogsResponse,
  RunEventsResponse,
} from '@/lib/types'

/**
 * Query key factory for runs
 */
export const runKeys = {
  all: ['runs'] as const,
  lists: () => [...runKeys.all, 'list'] as const,
  list: (params: RunsFilterParams) => [...runKeys.lists(), params] as const,
  details: () => [...runKeys.all, 'detail'] as const,
  detail: (id: string) => [...runKeys.details(), id] as const,
  logs: (id: string) => [...runKeys.detail(id), 'logs'] as const,
  events: (id: string) => [...runKeys.detail(id), 'events'] as const,
}

/**
 * Build query string from filter params
 */
function buildQueryString(params: RunsFilterParams): string {
  const searchParams = new URLSearchParams()
  
  if (params.status?.length) {
    params.status.forEach(s => searchParams.append('status', s))
  }
  if (params.search) {
    searchParams.set('search', params.search)
  }
  if (params.start_date) {
    searchParams.set('start_date', params.start_date)
  }
  if (params.end_date) {
    searchParams.set('end_date', params.end_date)
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', String(params.offset))
  }
  if (params.limit !== undefined) {
    searchParams.set('limit', String(params.limit))
  }
  if (params.projectId) {
    searchParams.set('projectId', params.projectId)
  }
  
  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Hook to fetch paginated list of runs with filters
 * 
 * @param params - Filter parameters
 * @returns TanStack Query result with runs list
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useRuns({ 
 *   status: ['RUNNING', 'QUEUED'],
 *   limit: 10 
 * })
 * ```
 */
export function useRuns(params: RunsFilterParams = {}) {
  return useQuery({
    queryKey: runKeys.list(params),
    queryFn: () => get<RunsListResponse>(`/runs${buildQueryString(params)}`),
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 10 * 1000, // Auto-refresh every 10s
  })
}

/**
 * Hook to fetch a single run by ID
 * 
 * @param id - Run ID
 * @returns TanStack Query result with run details
 * 
 * @example
 * ```tsx
 * const { data: run, isLoading } = useRun('run_20251219_143052_a1b2c3d4')
 * ```
 */
export function useRun(id: string) {
  return useQuery({
    queryKey: runKeys.detail(id),
    queryFn: () => get<Run>(`/runs/${id}`),
    enabled: !!id,
    staleTime: 5 * 1000, // 5 seconds for individual runs
    refetchInterval: (query) => {
      // Auto-refresh only for active runs
      const run = query.state.data
      if (run && ['PENDING', 'QUEUED', 'RUNNING'].includes(run.status)) {
        return 5 * 1000
      }
      return false
    },
  })
}

/**
 * Hook to fetch logs for a run
 * 
 * @param id - Run ID
 * @param enabled - Whether to fetch logs (default: true)
 * @returns TanStack Query result with run logs
 * 
 * @example
 * ```tsx
 * const { data: logs } = useRunLogs('run_20251219_143052_a1b2c3d4')
 * ```
 */
export function useRunLogs(id: string, enabled = true) {
  return useQuery({
    queryKey: runKeys.logs(id),
    queryFn: () => get<RunLogsResponse>(`/runs/${id}/logs`),
    enabled: !!id && enabled,
    staleTime: 0, // Always refetch logs
    refetchInterval: (query) => {
      // Auto-refresh logs if still streaming
      const logs = query.state.data
      if (logs?.is_streaming) {
        return 2 * 1000
      }
      return false
    },
  })
}

/**
 * Hook to fetch agent events for a run
 * 
 * @param id - Run ID
 * @param enabled - Whether to fetch events (default: true)
 * @returns TanStack Query result with run events
 * 
 * @example
 * ```tsx
 * const { data: events } = useRunEvents('run_20251219_143052_a1b2c3d4')
 * ```
 */
export function useRunEvents(id: string, enabled = true) {
  return useQuery({
    queryKey: runKeys.events(id),
    queryFn: () => get<RunEventsResponse>(`/runs/${id}/events`),
    enabled: !!id && enabled,
    staleTime: 30 * 1000, // Events don't change after run completion
  })
}

/**
 * Hook to create a new run
 * 
 * @returns TanStack mutation for creating runs
 * 
 * @example
 * ```tsx
 * const createRun = useCreateRun()
 * 
 * const handleSubmit = async (data: CreateRunRequest) => {
 *   const run = await createRun.mutateAsync(data)
 *   navigate(`/runs/${run.run_id}`)
 * }
 * ```
 */
export function useCreateRun() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: CreateRunRequest) => post<Run>('/runs', data),
    onSuccess: () => {
      // Invalidate runs list to show new run
      queryClient.invalidateQueries({ queryKey: runKeys.lists() })
    },
  })
}

/**
 * Hook to cancel a running run
 * 
 * @returns TanStack mutation for canceling runs
 * 
 * @example
 * ```tsx
 * const cancelRun = useCancelRun()
 * 
 * const handleCancel = async (id: string) => {
 *   await cancelRun.mutateAsync(id)
 * }
 * ```
 */
export function useCancelRun() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: string) => post<Run>(`/runs/${id}/cancel`),
    onSuccess: (data, id) => {
      // Update the run in cache
      queryClient.setQueryData(runKeys.detail(id), data)
      // Invalidate lists to reflect status change
      queryClient.invalidateQueries({ queryKey: runKeys.lists() })
    },
  })
}

/**
 * Hook to get run counts by status for dashboard
 * 
 * @returns Counts for each status category
 * 
 * @example
 * ```tsx
 * const { data } = useRunCounts()
 * // data = { active: 3, queued: 7, failedToday: 2 }
 * ```
 */
export function useRunCounts() {
  const { data: runsData } = useRuns({ limit: 1000 })
  
  const counts = {
    active: 0,
    queued: 0,
    failedToday: 0,
  }
  
  if (runsData?.runs) {
    const today = new Date().toISOString().split('T')[0]
    
    runsData.runs.forEach(run => {
      if (run.status === 'RUNNING') {
        counts.active++
      } else if (run.status === 'QUEUED' || run.status === 'PENDING') {
        counts.queued++
      } else if (run.status === 'FAILED' && run.timestamps?.created?.startsWith(today)) {
        counts.failedToday++
      }
    })
  }
  
  return counts
}

